import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  VStack,
  InputGroup,
  InputLeftElement,
  Input,
  Text,
  Flex,
  HStack,
  Box,
  Divider,
  Avatar,
  Kbd,
} from '@chakra-ui/react'
import { SearchIcon, ArrowForwardIcon } from '@chakra-ui/icons'
import { useState, useMemo, useEffect, useCallback } from 'react'
import type { PrescriberJoinedLocationProfile } from '../../../types/location'
import { usePrescriberLocationProfileSearch } from '../hooks/usePrescriberLocationProfileSearch'

interface SelectLocationModalProps {
  isOpen: boolean
  joinedPrescriberLocationProfiles: PrescriberJoinedLocationProfile[]
  prescriberName: string
  title: string
  prescriberNumber: string
  onSelect: (locationProfileId: string) => void
  onJoinNew: () => void
}

function getDefaultLocationProfileId(profiles: PrescriberJoinedLocationProfile[]): string | null {
  const defaultProfile = profiles.find((p) => p.location.isDefault)
  if (defaultProfile) return defaultProfile.id
  return profiles.length > 0 ? profiles[0].id : null
}

export const SelectLocationModal = ({
  isOpen,
  joinedPrescriberLocationProfiles,
  prescriberName,
  title,
  prescriberNumber,
  onSelect,
  onJoinNew,
}: SelectLocationModalProps) => {
  const { searchQuery, setSearchQuery, filteredProfiles } = usePrescriberLocationProfileSearch(
    joinedPrescriberLocationProfiles
  )

  const defaultLocationProfileId = useMemo(
    () => getDefaultLocationProfileId(joinedPrescriberLocationProfiles),
    [joinedPrescriberLocationProfiles]
  )

  const [selectedLocationProfileId, setSelectedLocationProfileId] = useState<string | null>(
    defaultLocationProfileId
  )

  useEffect(() => {
    if (defaultLocationProfileId && !selectedLocationProfileId) {
      setSelectedLocationProfileId(defaultLocationProfileId)
    }
  }, [defaultLocationProfileId, selectedLocationProfileId])

  const selectedPrescriberLocationProfile = useMemo(() => {
    return joinedPrescriberLocationProfiles.find((p) => p.id === selectedLocationProfileId) ?? null
  }, [joinedPrescriberLocationProfiles, selectedLocationProfileId])

  const handleStartSession = useCallback(() => {
    if (selectedPrescriberLocationProfile) {
      onSelect(selectedPrescriberLocationProfile.id)
    }
  }, [selectedPrescriberLocationProfile, onSelect])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || filteredProfiles.length === 0) return

      const currentIndex = filteredProfiles.findIndex((p) => p.id === selectedLocationProfileId)

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const nextIndex = currentIndex < filteredProfiles.length - 1 ? currentIndex + 1 : 0
        setSelectedLocationProfileId(filteredProfiles[nextIndex].id)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredProfiles.length - 1
        setSelectedLocationProfileId(filteredProfiles[prevIndex].id)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        handleStartSession()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, filteredProfiles, selectedLocationProfileId, handleStartSession])

  const getInitials = (name: string) => {
    if (name.length >= 2) {
      return `${name[0]}${name[name.length - 1]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      size="lg"
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="2xl" bg="white">
        <Box bg="blue.900" color="white" px={8} py={8}>
          <HStack spacing={4} mb={8}>
            <Avatar
              size="md"
              name={prescriberName}
              bg="blue.500"
              color="white"
              fontWeight="bold"
              getInitials={() => getInitials(prescriberName)}
            />
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="md">
                {title} {prescriberName}
              </Text>
              <Text fontSize="sm" color="gray.400" fontWeight="medium">
                Prescriber {prescriberNumber}
              </Text>
            </VStack>
          </HStack>

          <Text fontSize="xl" fontWeight="bold" mb={2} letterSpacing="tight">
            Where are you working today?
          </Text>
          <Text fontSize="sm" color="gray.400" fontWeight="medium">
            Select your location to start your session
          </Text>
        </Box>

        {/* Body Section */}
        <ModalBody px={8} py={6}>
          <VStack spacing={4} align="stretch">
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none" color="gray.400">
                <SearchIcon />
              </InputLeftElement>
              <Input
                placeholder="Search locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                borderRadius="lg"
                borderColor="blue.400"
                borderWidth="2px"
                _hover={{ borderColor: 'blue.500' }}
                _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                fontWeight="medium"
              />
            </InputGroup>

            <Flex
              justify="space-between"
              align="center"
              fontSize="sm"
              color="gray.400"
              fontWeight="medium"
              pt={1}
            >
              <HStack spacing={4}>
                <HStack spacing={1.5}>
                  <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs">
                    ↑
                  </Kbd>
                  <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs">
                    ↓
                  </Kbd>
                  <Text>navigate</Text>
                </HStack>
                <HStack spacing={1.5}>
                  <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs" px={2}>
                    ↵
                  </Kbd>
                  <Text>select</Text>
                </HStack>
              </HStack>
              <Text>
                {filteredProfiles.length} location{filteredProfiles.length !== 1 ? 's' : ''}
              </Text>
            </Flex>

            <VStack
              align="stretch"
              spacing={2}
              maxH="220px"
              overflowY="auto"
              css={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { width: '6px' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'var(--chakra-colors-gray-200)',
                  borderRadius: '24px',
                },
              }}
            >
              {filteredProfiles.map((profile) => {
                const isSelected = selectedLocationProfileId === profile.id
                return (
                  <Flex
                    key={profile.id}
                    p={4}
                    borderWidth="1px"
                    borderColor={isSelected ? 'blue.100' : 'gray.200'}
                    borderRadius="lg"
                    bg={isSelected ? 'blue.50' : 'white'}
                    cursor="pointer"
                    onClick={() => setSelectedLocationProfileId(profile.id)}
                    align="center"
                    justify="space-between"
                    _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
                    transition="all 0.2s"
                  >
                    <Text
                      fontWeight="bold"
                      fontSize="sm"
                      color={isSelected ? 'blue.600' : 'gray.700'}
                    >
                      {profile.location.name}
                    </Text>
                    <HStack spacing={4}>
                      {profile.location.isDefault && (
                        <Box
                          bg="teal.50"
                          color="teal.700"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          Default
                        </Box>
                      )}
                      <Text fontSize="sm" color="gray.400" fontWeight="medium">
                        {profile.location.suburb}
                      </Text>
                      <Box
                        w={5}
                        h={5}
                        borderRadius="full"
                        borderWidth="2px"
                        borderColor={isSelected ? 'blue.400' : 'gray.300'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg={isSelected ? 'blue.400' : 'transparent'}
                      >
                        {isSelected && <Box w={2} h={2} borderRadius="full" bg="white" />}
                      </Box>
                    </HStack>
                  </Flex>
                )
              })}
            </VStack>
          </VStack>
        </ModalBody>

        {/* Footer Section */}
        <Box px={8} pb={6}>
          <Divider borderColor="gray.200" mb={6} />

          <Flex w="full" justify="space-between" align="center" mb={6}>
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="wider">
                SELECTED
              </Text>
              <Text fontWeight="bold" fontSize="lg" color="gray.900">
                {selectedPrescriberLocationProfile
                  ? selectedPrescriberLocationProfile.location.name
                  : 'None'}
              </Text>
            </VStack>
            <Button
              colorScheme="blue"
              size="md"
              onClick={handleStartSession}
              isDisabled={!selectedPrescriberLocationProfile}
              rightIcon={<ArrowForwardIcon />}
              px={8}
              borderRadius="lg"
              fontWeight="bold"
            >
              Start session
            </Button>
          </Flex>

          <Divider borderColor="gray.200" mb={4} />

          <Text fontSize="sm" color="gray.500" textAlign="center" fontWeight="medium">
            Can't find your location?{' '}
            <Button
              variant="link"
              color="blue.500"
              onClick={onJoinNew}
              fontWeight="bold"
              fontSize="sm"
            >
              Join a new location
            </Button>
          </Text>
        </Box>
      </ModalContent>
    </Modal>
  )
}
